import { Select, type UseFormReturn } from '@faceup/form'
import { FormItemType } from '@faceup/utils'
import { yup } from '@faceup/validation'
import { useMemo } from 'react'
import { sharedMessages } from '../../../Shared/translations'
import { defineMessages, useIntl } from '../../../TypedIntl'

const messages = defineMessages({
  institutionLabel: 'Shared.report.organization',
})

type OrganizationalUnit = {
  id: string
  organizationalUnitName: string | null
}

type OrganizationalUnitItem = {
  id: string
  isRequired?: boolean
  labelTranslation?: { translation?: string | null } | null
  hintTranslation?: { translation?: string | null } | null
}

type UseAF_OrganizationalUnitSelectParams = {
  organizationalUnitItem: OrganizationalUnitItem | undefined
  selectableOrganizationUnits: OrganizationalUnit[]
  selectedOrgUnit?: string
  onSelectionChange?: (value: string | undefined) => void
}

export const useAF_OrganizationalUnitSelect = ({
  organizationalUnitItem,
  selectableOrganizationUnits,
  selectedOrgUnit,
  onSelectionChange,
}: UseAF_OrganizationalUnitSelectParams) => {
  const { formatMessage } = useIntl()

  return useMemo(() => {
    if (!organizationalUnitItem) {
      return null
    }

    const orgUnitSchema = yup.object({
      type: yup.string().oneOf([FormItemType.OrganizationalUnit]).required(),
      value: organizationalUnitItem.isRequired
        ? yup
            .string()
            .required()
            .oneOf(selectableOrganizationUnits.map(u => u.id))
        : yup.string().oneOf(selectableOrganizationUnits.map(u => u.id)),
    })

    const defaultValue =
      selectableOrganizationUnits.length === 1
        ? (selectableOrganizationUnits[0]?.id ?? undefined)
        : selectedOrgUnit

    // Only add the field if there are multiple units (field will be shown)
    // But always set the default value so single unit is preselected
    if (selectableOrganizationUnits.length > 1) {
      return {
        id: organizationalUnitItem.id,
        schema: orgUnitSchema,
        defaultValue: {
          type: FormItemType.OrganizationalUnit,
          value: defaultValue,
        },
        render: (form: UseFormReturn) => (
          <Select
            control={form.control}
            name={`${organizationalUnitItem.id}.value`}
            onChange={onSelectionChange}
            label={
              organizationalUnitItem.labelTranslation?.translation ??
              formatMessage(messages.institutionLabel)
            }
            hint={organizationalUnitItem.hintTranslation?.translation}
            withAsterisk={organizationalUnitItem.isRequired ?? false}
            placeholder={formatMessage(sharedMessages.selectPlaceholder)}
            allowClear
            data-test='create-company-report-form-company-input'
            popupRender={menu => (
              <div data-test='create-company-report-form-company-menu'>{menu}</div>
            )}
            className='w-full'
            options={selectableOrganizationUnits
              .filter(unit => unit.organizationalUnitName != null)
              .map(unit => ({
                value: unit.id,
                label: unit.organizationalUnitName ?? '',
                'data-test': 'create-company-report-form-company-item',
              }))}
            formItemProps={{
              'data-test': 'create-company-report-form-company',
            }}
          />
        ),
      }
    }

    if (selectableOrganizationUnits.length === 1 && defaultValue) {
      return {
        id: organizationalUnitItem.id,
        schema: orgUnitSchema,
        defaultValue: {
          type: FormItemType.OrganizationalUnit,
          value: defaultValue,
        },
        render: () => null,
      }
    }

    return null
  }, [
    organizationalUnitItem,
    selectableOrganizationUnits,
    formatMessage,
    onSelectionChange,
    selectedOrgUnit,
  ])
}
